import {
  distinctUntilChanged,
  EMPTY,
  expand,
  fromEvent,
  map,
  merge,
  of,
  retry,
  share,
  switchMap,
  takeUntil,
  takeWhile,
  timer,
} from "rxjs";
import { fetchVideoJobDetail$ } from "@apis/http/api";
import type { JobDetailData } from "@apis/videoCreatingService";
import { UserAiJobItemResponseStatus } from "@apis/generated/openapi";

export const POLL_INTERVAL = 10 * 1000;
export const POLL_MAX_DURATION = 90 * 60 * 1000;
export const POLL_RETRY_COUNT = 3;

const isTerminal = (status?: UserAiJobItemResponseStatus) =>
  status === UserAiJobItemResponseStatus.completed || status === UserAiJobItemResponseStatus.failed;

export interface VideoJobLiveStatus {
  status?: UserAiJobItemResponseStatus;
  errorMessage?: string;
  videoUrl?: string;
  coverImageUrl?: string;
}

const toLiveStatus = (detail: JobDetailData): VideoJobLiveStatus => ({
  status: detail.state as UserAiJobItemResponseStatus,
  errorMessage: detail.errorMessage ?? undefined,
  videoUrl: detail.output?.videoUrl,
  coverImageUrl: detail.output?.coverImageUrl,
});

/** 单次拉取任务详情并映射为 VideoJobLiveStatus（defineRoute$ 无 .as<R>()，沿用 as unknown as 转换） */
const fetchStatus$ = (jobId: number | string) =>
  fetchVideoJobDetail$({ jobId: Number(jobId) }).pipe(map((res) => toLiveStatus(res as unknown as JobDetailData)));

/**
 * 串行轮询：expand 在上一响应返回后才 schedule 下一次拉取，因此天然背压——
 * 每张卡片至多只有一个在途请求，间隔从「响应完成」起算；命中终态返回 EMPTY 停止。
 */
const serialPoll$ = (jobId: number | string) =>
  fetchStatus$(jobId).pipe(
    expand((s) => (isTerminal(s.status) ? EMPTY : timer(POLL_INTERVAL).pipe(switchMap(() => fetchStatus$(jobId))))),
    retry({ count: POLL_RETRY_COUNT, delay: () => timer(POLL_INTERVAL), resetOnSuccess: true }),
  );

/** 页面可见性流：隐藏 → false（暂停轮询），切回 → true（switchMap 重订阅即立即补拉一次）。
 *  share() 使所有轮询卡片共享同一个 DOM 监听器，而非每张卡片各注册一个。*/
const visible$ = merge(of(null), fromEvent(document, "visibilitychange")).pipe(
  map(() => document.visibilityState === "visible"),
  distinctUntilChanged(),
  share(),
);

/**
 * 单卡片任务状态轮询（RxJS）：可见时串行轮询 job-detail，隐藏时暂停、切回立即补拉；
 * 命中终态（completed/failed）后整体完成（takeWhile inclusive），可见性切换不会重启已结束的轮询；
 * 90 分钟兜底超时。纯 Observable，不含 React/Redux 副作用。
 */
export const createVideoJobStatusPoll$ = (jobId: number | string) =>
  visible$.pipe(
    switchMap((visible) => (visible ? serialPoll$(jobId) : EMPTY)),
    takeWhile((s) => !isTerminal(s.status), true),
    takeUntil(timer(POLL_MAX_DURATION)),
  );
